<template>
  <ContentWrap>
    <el-form :inline="true" :model="query" class="mb-12px" @submit.prevent>
      <el-form-item label="举报人">
        <el-input v-model="query.reporterNickname" clearable class="!w-160px" placeholder="昵称关键字" />
      </el-form-item>
      <el-form-item label="被举报人">
        <el-input v-model="query.targetNickname" clearable class="!w-160px" placeholder="昵称关键字" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.auditStatus" clearable class="!w-140px">
          <el-option label="待处理" :value="0" />
          <el-option label="成立" :value="1" />
          <el-option label="驳回" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">查询</el-button>
        <el-button @click="reset">重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column label="举报人" min-width="140">
        <template #default="{ row }">
          <div class="user-cell">
            <el-avatar :size="28" :src="row.reporterAvatar" />
            <span>{{ row.reporterNickname || row.reporterId }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="被举报人" min-width="140">
        <template #default="{ row }">
          <div class="user-cell">
            <el-avatar :size="28" :src="row.targetAvatar" />
            <span>{{ row.targetNickname || row.targetUserId }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="reportType" label="类型" min-width="140" show-overflow-tooltip />
      <el-table-column prop="content" label="说明" min-width="200" show-overflow-tooltip />
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <el-tag :type="row.auditStatus === 1 ? 'success' : row.auditStatus === 2 ? 'info' : 'warning'">
            {{ ['待处理', '成立', '驳回'][row.auditStatus] || '-' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="auditResult" label="处理结果" min-width="160" show-overflow-tooltip />
      <el-table-column prop="createTime" label="举报时间" min-width="170" :formatter="dateFormatter" />
      <el-table-column label="操作" width="140" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openView(row)">查看</el-button>
          <el-button
            v-if="row.auditStatus === 0"
            v-hasPermi="['bosscircle:report:handle']"
            link
            type="warning"
            @click="open(row)"
          >
            处理
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>

  <Dialog v-model="visible" :title="viewOnly ? '举报详情' : '处理举报'" width="520px">
    <el-form :model="form" label-width="90px">
      <el-form-item label="举报类型">
        <div>{{ current?.reportType || '-' }}</div>
      </el-form-item>
      <el-form-item label="举报内容">
        <div class="text-13px leading-22px text-#606266">{{ current?.content || '-' }}</div>
      </el-form-item>
      <el-form-item label="处理结论">
        <el-radio-group v-model="form.auditStatus" :disabled="viewOnly">
          <el-radio :value="1">举报成立</el-radio>
          <el-radio :value="2">驳回举报</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="处理说明">
        <el-input
          v-model="form.auditResult"
          type="textarea"
          :rows="4"
          maxlength="1024"
          show-word-limit
          :disabled="viewOnly"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="visible = false">关闭</el-button>
      <el-button v-if="!viewOnly" type="primary" :loading="submitting" @click="submit">确定</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import * as Api from '@/api/business/report'
import { dateFormatter } from '@/utils/formatTime'

defineOptions({ name: 'BusinessReport' })

const message = useMessage()
const loading = ref(false)
const submitting = ref(false)
const visible = ref(false)
const viewOnly = ref(false)
const list = ref<Api.ReportVO[]>([])
const total = ref(0)
const current = ref<Api.ReportVO | null>(null)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  reporterNickname: '',
  targetNickname: '',
  auditStatus: undefined as number | undefined
})
const form = reactive({ id: 0, auditStatus: 1, auditResult: '' })

const load = async () => {
  loading.value = true
  try {
    const data = await Api.getReportPage(query)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const search = () => {
  query.pageNo = 1
  load()
}

const reset = () => {
  Object.assign(query, {
    pageNo: 1,
    pageSize: 10,
    reporterNickname: '',
    targetNickname: '',
    auditStatus: undefined
  })
  load()
}

const open = (row: Api.ReportVO) => {
  current.value = row
  viewOnly.value = false
  form.id = row.id!
  form.auditStatus = 1
  form.auditResult = ''
  visible.value = true
}

const openView = (row: Api.ReportVO) => {
  current.value = row
  viewOnly.value = true
  form.id = row.id!
  form.auditStatus = row.auditStatus || 1
  form.auditResult = row.auditResult || ''
  visible.value = true
}

const submit = async () => {
  submitting.value = true
  try {
    await Api.handleReport(form)
    message.success('处理成功')
    visible.value = false
    load()
  } finally {
    submitting.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.user-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
